import React from "react";
import moment from "moment";
import { ReactComponent as NurseryDetailIcon } from "../../../../../assets/icons/nursery-detail.svg";
import { ReactComponent as EducationDetailIcon } from "../../../../../assets/icons/education-detail.svg";
import { ReactComponent as HealthInsuranceDetailIcon } from "../../../../../assets/icons/health-insurance-detail.svg";
import { ReactComponent as LifeInsuranceDetailIcon } from "../../../../../assets/icons/life-insurance-detail.svg";
import { ReactComponent as RentingDetailIcon } from "../../../../../assets/icons/renting-detail.svg";
import { ReactComponent as WellnessDetailIcon } from "../../../../../assets/icons/wellness-detail.svg";
import RecurrentPayment from "../../../../domain/RecurrentPayment";
import AcceptEducationModal from "../../../../components/modals/recurrentPayments/AcceptEducationModal";
import RejectEducationModal from "../../../../components/modals/recurrentPayments/RejectEducationModal";
import RejectRentingModal from "../../../../components/modals/recurrentPayments/RejectRentingModal";
import AcceptRentingModal from "../../../../components/modals/recurrentPayments/AcceptRentingModal";
import { translate as t } from "../../../../../infrastructure/translate/localization";
import { MonetaryAmount } from "../../../../domain/monetary-amount";

export const recurrentPaymentDetailsConfig = (
  recurrentPayment: RecurrentPayment
) => {
  const {
    employee,
    state,
    recipient,
    nursery,
    course,
    centre,
    modality,
    insuranceCarrier,
    brand,
    amount,
    companyAmount,
    employeeAmount,
    approvals,
    healthModality,
    healthComplements,
    brochureUrl,
    effectiveDate,
    payrollNumber,
    initialCompanyAmount,
    initialEmployeeAmount,
    productName,
    trainingInfo,
  } = recurrentPayment;

  return {
    nursery: {
      Icon: NurseryDetailIcon,
      hasInfo: false,
      hasApprovers: false,
      titleText: nursery,
      subTitleText: t("benefits-section.nursery-benefit.nursery"),
      rows: [
        {
          label: t("common-messages.employee"),
          data: `${employee.name} ${employee.surname}`,
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: true,
          hasBreakdown: false,
          accordionData: {
            internalId: employee.internalId || "-",
            legalId: employee.legalId,
            email: employee.email,
          },
        },
        {
          label: t("common-messages.child"),
          data: `${recipient.name} ${recipient.surname}`,
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.monthly-fee"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: amount?.amountInCents,
            currency: amount?.currency,
          }).prettifyMoneyWithoutSymbol()}€/${t("common-messages.month")}`,
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "bold",
          hasAccordion: false,
          hasBreakdown: false,
        },
      ],
      tableColumns: [
        {
          dataKey: "date",
          label: t("recurrent-payments-section.payment-date"),
        },
        {
          dataKey: "quota",
          label: t("recurrent-payments-section.fee"),
        },
        {
          dataKey: "society",
          label: t("common-messages.society"),
        },
        { dataKey: "costCenter", label: t("common-messages.cost-center") },
      ],
      parseData: (data) =>
        data.map(
          ({
            userTransactionIsoTime: date,
            intendedAmount: quota,
            payrollCompany: society,
            costCenter,
          }) => ({
            date: `${moment(date).format("D")} ${moment(date).format(
              "MMM"
            )} ${moment(date).format("YYYY")}`,
            quota: `${MonetaryAmount.ofCurrency({
              amountInCents: quota?.amountInCents,
              currency: quota?.currency,
            }).prettifyMoneyWithoutSymbol()}€`,
            society: society || "-",
            costCenter: costCenter || "-",
          })
        ),
      hasDateRange: true,
      hasActions: false,
    },
    education: {
      Icon: EducationDetailIcon,
      hasInfo: false,
      hasApprovers: true,
      titleText: course,
      subTitleText: centre,
      rows: [
        {
          label: t("common-messages.employee"),
          data: `${employee.name} ${employee.surname}`,
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: true,
          hasBreakdown: false,
          accordionData: {
            internalId: employee.internalId || "-",
            legalId: employee.legalId,
            email: employee.email,
            trainingInfo,
          },
        },
        {
          label: t("recurrent-payments-section.course-price"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: amount?.amountInCents,
            currency: amount?.currency,
          }).prettifyMoneyWithoutSymbol()}€`,
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "bold",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-company-pays"),
          data: companyAmount
            ? `${MonetaryAmount.ofCurrency({
                amountInCents: companyAmount?.amountInCents,
                currency: companyAmount?.currency,
              }).prettifyMoneyWithoutSymbol()}€`
            : "-",
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-employee-pays"),
          data:
            employeeAmount && state !== "pending"
              ? `${MonetaryAmount.ofCurrency({
                  amountInCents: employeeAmount?.amountInCents,
                  currency: employeeAmount?.currency,
                }).prettifyMoneyWithoutSymbol()}€`
              : "-",
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          subLabel: payrollNumber
            ? `${t("common-messages.in")} ${payrollNumber} ${t(
                "common-messages.payroll"
              )}`
            : null,
          hasAccordion: false,
          hasBreakdown: false,
        },
      ],
      tableColumns: [
        {
          dataKey: "date",
          label: t("recurrent-payments-section.payment-date"),
        },
        {
          dataKey: "quota",
          label: t("recurrent-payments-section.fee"),
        },
        {
          dataKey: "society",
          label: t("common-messages.society"),
        },
        { dataKey: "costCenter", label: t("common-messages.cost-center") },
      ],
      parseData: (data) =>
        data.map(
          ({
            userTransactionIsoTime: date,
            intendedAmount: quota,
            payrollCompany: society,
            costCenter,
          }) => ({
            date: `${moment(date).format("D")} ${moment(date).format(
              "MMM"
            )} ${moment(date).format("YYYY")}`,
            quota: `${MonetaryAmount.ofCurrency({
              amountInCents: quota?.amountInCents,
              currency: quota?.currency,
            }).prettifyMoneyWithoutSymbol()}€`,
            society: society || "-",
            costCenter: costCenter || "-",
          })
        ),
      hasDateRange: true,
      hasActions: true,
      acceptAction: (open) =>
        open({
          title:
            approvals.length === 0
              ? t(
                  "recurrent-payments-section.approve-education-request-step-message",
                  {
                    step: 1,
                    type: t("common-messages.hr"),
                  }
                )
              : t(
                  "recurrent-payments-section.approve-education-request-step-message",
                  {
                    step: 2,
                    type: t("common-messages.finance"),
                  }
                ),
          modalContents: <AcceptEducationModal />,
        }),
      rejectAction: (open) =>
        open({
          title: t("recurrent-payments-section.reject-education-request"),
          modalContents: <RejectEducationModal />,
        }),
      acceptLabel:
        approvals.length === 0
          ? t("recurrent-payments-section.accept-step-message", { step: 1 })
          : t("recurrent-payments-section.accept-step-message", { step: 2 }),
      hasReasonBlock: true,
    },
    "health-insurance": {
      Icon: HealthInsuranceDetailIcon,
      hasInfo: true,
      policyUrl: brochureUrl,
      hasApprovers: false,
      titleText: modality,
      subTitleText: insuranceCarrier,
      rows: [
        {
          label: t("common-messages.employee"),
          data: `${employee.name} ${employee.surname}`,
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: true,
          hasBreakdown: false,
          accordionData: {
            internalId: employee.internalId || "-",
            legalId: employee.legalId,
            email: employee.email,
          },
        },
        {
          label: t("recurrent-payments-section.insurance-amount"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: amount?.amountInCents,
            currency: amount?.currency,
          }).prettifyMoneyWithoutSymbol()}€/${t("common-messages.month")}`,
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "bold",
          hasAccordion: false,
          hasBreakdown: true,
          breakdownData:
            healthModality !== null
              ? {
                  modalityName: healthModality.name,
                  modalityFee: MonetaryAmount.ofCurrency({
                    amountInCents: healthModality.modalityFee?.amountInCents,
                    currency: healthModality.modalityFee?.currency,
                  }).prettifyMoneyWithoutSymbol(),
                  complements: healthComplements,
                }
              : null,
        },
        {
          label: t("recurrent-payments-section.the-company-pays"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: companyAmount?.amountInCents,
            currency: companyAmount?.currency,
          }).prettifyMoneyWithoutSymbol()}€/${t("common-messages.month")}`,
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-employee-pays"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: employeeAmount?.amountInCents,
            currency: employeeAmount?.currency,
          }).prettifyMoneyWithoutSymbol()}€/${t("common-messages.month")}`,
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.effect-date"),
          data: effectiveDate
            ? `${moment(effectiveDate).format("D")} ${moment(
                effectiveDate
              ).format("MMM")} ${moment(effectiveDate).format("YYYY")}`
            : "-",
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
      ],
      tableColumns: [
        {
          dataKey: "date",
          label: t("recurrent-payments-section.payment-date"),
        },
        {
          dataKey: "quota",
          label: t("recurrent-payments-section.fee"),
        },
        {
          dataKey: "society",
          label: t("common-messages.society"),
        },
        { dataKey: "costCenter", label: t("common-messages.cost-center") },
      ],
      parseData: (data) =>
        data.map(
          ({
            userTransactionIsoTime: date,
            intendedAmount: quota,
            payrollCompany: society,
            costCenter,
          }) => ({
            date: `${moment(date).format("D")} ${moment(date).format(
              "MMM"
            )} ${moment(date).format("YYYY")}`,
            quota: `${MonetaryAmount.ofCurrency({
              amountInCents: quota?.amountInCents,
              currency: quota?.currency,
            }).prettifyMoneyWithoutSymbol()}€`,
            society: society || "-",
            costCenter: costCenter || "-",
          })
        ),
      hasDateRange: false,
      hasBeneficiaries: true,
      hasActions: false,
    },
    "life-insurance": {
      Icon: LifeInsuranceDetailIcon,
      hasInfo: true,
      policyUrl: brochureUrl,
      hasApprovers: false,
      titleText: modality,
      subTitleText: insuranceCarrier,
      rows: [
        {
          label: t("common-messages.employee"),
          data: `${employee.name} ${employee.surname}`,
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: true,
          hasBreakdown: false,
          accordionData: {
            internalId: employee.internalId || "-",
            legalId: employee.legalId,
            email: employee.email,
          },
        },
        {
          label: t("recurrent-payments-section.insured-capital"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: amount?.amountInCents,
            currency: amount?.currency,
          }).prettifyMoneyWithoutSymbol()}€`,
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "bold",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-company-pays"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: companyAmount?.amountInCents,
            currency: companyAmount?.currency,
          }).prettifyMoneyWithoutSymbol()}€/${t("common-messages.month")}`,
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-employee-pays"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: employeeAmount?.amountInCents,
            currency: employeeAmount?.currency,
          }).prettifyMoneyWithoutSymbol()}€/${t("common-messages.month")}`,
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.effect-date"),
          data: effectiveDate
            ? `${moment(effectiveDate).format("D")} ${moment(
                effectiveDate
              ).format("MMM")} ${moment(effectiveDate).format("YYYY")}`
            : "-",
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
      ],
      tableColumns: [
        {
          dataKey: "date",
          label: t("recurrent-payments-section.payment-date"),
        },
        {
          dataKey: "quota",
          label: t("recurrent-payments-section.fee"),
        },
        {
          dataKey: "society",
          label: t("common-messages.society"),
        },
        { dataKey: "costCenter", label: t("common-messages.cost-center") },
      ],
      parseData: (data) =>
        data.map(
          ({
            userTransactionIsoTime: date,
            intendedAmount: quota,
            payrollCompany: society,
            costCenter,
          }) => ({
            date: `${moment(date).format("D")} ${moment(date).format(
              "MMM"
            )} ${moment(date).format("YYYY")}`,
            quota: `${MonetaryAmount.ofCurrency({
              amountInCents: quota?.amountInCents,
              currency: quota?.currency,
            }).prettifyMoneyWithoutSymbol()}€`,
            society: society || "-",
            costCenter: costCenter || "-",
          })
        ),
      hasDateRange: false,
      hasActions: false,
    },
    renting: {
      Icon: RentingDetailIcon,
      hasRentingInfo: true,
      hasApprovers: false,
      titleText: brand,
      hasWhyQuestion: true,
      rows: [
        {
          label: t("common-messages.employee"),
          data: `${employee.name} ${employee.surname}`,
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: true,
          hasBreakdown: false,
          accordionData: {
            internalId: employee.internalId || "-",
            legalId: employee.legalId,
            email: employee.email,
          },
        },
        {
          label: t("recurrent-payments-section.initial-payment-price"),
          data:
            initialCompanyAmount?.amountInCents !== -1 &&
            initialEmployeeAmount?.amountInCents !== -1
              ? `${MonetaryAmount.ofCurrency({
                  amountInCents:
                    initialCompanyAmount?.amountInCents +
                    initialEmployeeAmount?.amountInCents,
                  currency: initialCompanyAmount?.currency,
                }).prettifyMoneyWithoutSymbol()}€`
              : "-",
          borderType: "slim",
          labelStyle: "bold",
          dataStyle: "bold",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-company-pays"),
          data:
            initialCompanyAmount?.amountInCents !== -1
              ? `${MonetaryAmount.ofCurrency({
                  amountInCents: initialCompanyAmount?.amountInCents,
                  currency: initialCompanyAmount?.currency,
                }).prettifyMoneyWithoutSymbol()}€`
              : "-",
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-employee-pays"),
          data:
            initialEmployeeAmount?.amountInCents !== -1
              ? `${MonetaryAmount.ofCurrency({
                  amountInCents: initialEmployeeAmount?.amountInCents,
                  currency: initialEmployeeAmount?.currency,
                }).prettifyMoneyWithoutSymbol()}€`
              : "-",
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.renting-price"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: amount?.amountInCents,
            currency: amount?.currency,
          }).prettifyMoneyWithoutSymbol()}€/${t("common-messages.month")}`,
          borderType: "slim",
          labelStyle: "bold",
          dataStyle: "bold",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-company-pays"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: companyAmount?.amountInCents,
            currency: companyAmount?.currency,
          }).prettifyMoneyWithoutSymbol()}€/${t("common-messages.month")}`,
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-employee-pays"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: employeeAmount?.amountInCents,
            currency: employeeAmount?.currency,
          }).prettifyMoneyWithoutSymbol()}€/${t("common-messages.month")}`,
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
      ],
      tableColumns: [
        {
          dataKey: "date",
          label: t("recurrent-payments-section.payment-date"),
        },
        {
          dataKey: "quota",
          label: t("recurrent-payments-section.fee"),
        },
        {
          dataKey: "society",
          label: t("common-messages.society"),
        },
        { dataKey: "costCenter", label: t("common-messages.cost-center") },
      ],
      parseData: (data) =>
        data.map(
          ({
            userTransactionIsoTime: date,
            intendedAmount: quota,
            payrollCompany: society,
            costCenter,
          }) => ({
            date: `${moment(date).format("D")} ${moment(date).format(
              "MMM"
            )} ${moment(date).format("YYYY")}`,
            quota: `${MonetaryAmount.ofCurrency({
              amountInCents: quota?.amountInCents,
              currency: quota?.currency,
            }).prettifyMoneyWithoutSymbol()}€`,
            society: society || "-",
            costCenter: costCenter || "-",
          })
        ),
      hasDateRange: true,
      hasActions: true,
      acceptAction: (open) =>
        open({
          title: t("recurrent-payments-section.approve-renting-request"),
          modalContents: <AcceptRentingModal />,
        }),
      rejectAction: (open) =>
        open({
          title: t("recurrent-payments-section.reject-renting-request"),
          modalContents: <RejectRentingModal />,
        }),
      acceptLabel: t("actions.accept"),
    },
    wellness: {
      Icon: WellnessDetailIcon,
      hasApprovers: false,
      titleText: productName,
      rows: [
        {
          label: t("common-messages.employee"),
          data: `${employee.name} ${employee.surname}`,
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: true,
          hasBreakdown: false,
          accordionData: {
            internalId: employee.internalId || "-",
            legalId: employee.legalId,
            email: employee.email,
          },
        },
        {
          label: t("recurrent-payments-section.product-price"),
          data: `${MonetaryAmount.ofCurrency({
            amountInCents: amount?.amountInCents,
            currency: amount?.currency,
          }).prettifyMoneyWithoutSymbol()}€`,
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "bold",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-company-pays"),
          data: companyAmount
            ? `${MonetaryAmount.ofCurrency({
                amountInCents: companyAmount?.amountInCents,
                currency: companyAmount?.currency,
              }).prettifyMoneyWithoutSymbol()}€`
            : "-",
          borderType: "slim",
          labelStyle: "standar",
          dataStyle: "standar",
          hasAccordion: false,
          hasBreakdown: false,
        },
        {
          label: t("recurrent-payments-section.the-employee-pays"),
          data:
            employeeAmount && state !== "pending"
              ? `${MonetaryAmount.ofCurrency({
                  amountInCents: employeeAmount?.amountInCents,
                  currency: employeeAmount?.currency,
                }).prettifyMoneyWithoutSymbol()}€`
              : "-",
          borderType: "fat",
          labelStyle: "standar",
          dataStyle: "standar",
          subLabel: payrollNumber
            ? `${t("common-messages.in")} ${payrollNumber} ${t(
                "common-messages.payroll"
              )}`
            : null,
          hasAccordion: false,
          hasBreakdown: false,
        },
      ],
      tableColumns: [
        {
          dataKey: "date",
          label: t("recurrent-payments-section.payment-date"),
        },
        {
          dataKey: "quota",
          label: t("recurrent-payments-section.fee"),
        },
        {
          dataKey: "society",
          label: t("common-messages.society"),
        },
        { dataKey: "costCenter", label: t("common-messages.cost-center") },
      ],
      parseData: (data) =>
        data.map(
          ({
            userTransactionIsoTime: date,
            intendedAmount: quota,
            payrollCompany: society,
            costCenter,
          }) => ({
            date: `${moment(date).format("D")} ${moment(date).format(
              "MMM"
            )} ${moment(date).format("YYYY")}`,
            quota: `${MonetaryAmount.ofCurrency({
              amountInCents: quota?.amountInCents,
              currency: quota?.currency,
            }).prettifyMoneyWithoutSymbol()}€`,
            society: society || "-",
            costCenter: costCenter || "-",
          })
        ),
      hasDateRange: true,
      hasActions: false,
      hasBeneficiaries: true,
    },
  };
};
